import { fg } from '@atlaskit/platform-feature-flags/fg';
import { addSentryBreadcrumb } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import { interactionMetrics } from '../../common/utils/browser-metrics/interactionMetrics';
import { fetchConversationChannelsData } from './fetchConversationChannelsData';
import { fetchInitialConversationChannelsOnce } from './fetchInitialConversationChannelsOnce';
import { hydrateConversationWithExternalParams } from './hydrateConversationWithExternalParams';
import { resetChatStoresForSite } from './resetChatStoresForSite';
import type { ChatStoreAction } from './types';

export const initializeChatStore =
	(): ChatStoreAction =>
	async (
		{ dispatch, getState, setState },
		{ setChatStatus, skipAutoConversationSelection, siteId, forceRestoreConversationId },
	) => {
		addSentryBreadcrumb('ChatStoreContainer: storeInit');
		setChatStatus('launching');

		const isSiteSwitchResetEnabled = fg('rovo_chat_site_switch_reset');
		const previousSiteId = getState().hydratedSiteId;
		const isSiteChange =
			isSiteSwitchResetEnabled &&
			Boolean(siteId) &&
			previousSiteId !== undefined &&
			previousSiteId !== siteId;

		if (isSiteSwitchResetEnabled) {
			void Promise.resolve().then(() => {
				setState({ hydratedSiteId: siteId });
			});
		}

		// Init conversation hydrates the chat store with conversation from URL or Storage
		// and fetches conversations list (however, fetching conversations list is performed "in the background")
		// conversations list is fetched to be later on displayed in the sidebar/conversation list to remove/minimize
		// visual delay when user opens the side-navigation; or in full-screen mode to fetch conversations as early as possible;
		const initConversation = async () => {
			if (isSiteChange) {
				await dispatch(resetChatStoresForSite({ siteId }));
			}
			dispatch(
				hydrateConversationWithExternalParams({
					autoSelectConversation: !skipAutoConversationSelection,
					forceRestoreConversationId,
				}),
			);
			interactionMetrics.initializeChannels.start();
			if (fg('rovo_chat_dedupe_initial_conversations_fetch')) {
				await dispatch(fetchInitialConversationChannelsOnce());
			} else {
				await dispatch(
					fetchConversationChannelsData({
						isInitialLoad: true,
					}),
				);
			}
			interactionMetrics.initializeChannels.stop();
		};

		void initConversation();

		addSentryBreadcrumb('ChatStoreContainer: storeInit completed');
	};
